import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, FileText, Database, BarChart3, CheckCircle2, Globe,
  Layers, RefreshCw, Link2, ShieldCheck,
  ExternalLink, Play, Bell, CalendarDays, LayoutDashboard, ChevronDown
} from 'lucide-react';
import ScrollHint from '@/components/common/ScrollHint';
import PageLayout from '@/components/layout/PageLayout';

const PRODUCTS_FAQ = [
  { q: 'What is Taxera?', a: 'Taxera is an enterprise-grade cloud platform that helps multinational companies manage global indirect tax compliance, including e-Invoicing, SAF-T, VAT returns, and tax reconciliation.' },
  { q: 'Which ERP systems does Taxera integrate with?', a: 'Taxera integrates natively with SAP, Oracle, and Microsoft Dynamics, as well as other standard and non-standard ERP systems.' },
  { q: 'Is Taxera compliant with GDPR and data privacy regulations?', a: 'Yes. Taxera is ISO 27001 certified, GDPR compliant, and gives clients full flexibility over their data processing location and hyperscaler.' },
  { q: 'Which countries does Taxera cover?', a: 'Taxera covers tax compliance obligations across a growing number of countries and jurisdictions worldwide, and is continuously updated as new regulations are introduced globally.' },
  { q: 'What is e-Invoicing and why does it matter?', a: 'e-Invoicing is the digital exchange of invoices directly between companies and tax authorities. It is becoming mandatory across the EU and globally under regulations like ViDA.' },
  { q: 'How long does implementation take?', a: 'Implementation timelines vary by complexity, but Taxera is designed for rapid deployment with pre-built ERP connectors and a dedicated onboarding team.' },
  { q: 'Who is Taxera built for?', a: 'Taxera is built for tax directors, CFOs, and finance teams at multinational corporations who need a single, reliable hub for all indirect tax obligations.' },
];

const ProductsFAQItem = ({ question, answer, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`products-faq-item-${index}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors">
        <span className="text-sm font-semibold text-taxera-charcoal pr-4">{question}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
            <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════
   DATA: Hubs within the Taxera Platform
   ═══════════════════════════════════════════ */

const PLATFORM_HUBS = [
  {
    id: 'command-center-hub',
    name: 'Command Center',
    tagline: 'Your global tax function — informed, on schedule, in control.',
    icon: LayoutDashboard,
    color: '#ec4899',
    description: 'Beyond compliance processing — a complete operational intelligence layer. Configurable notifications, a global filing calendar, and a unified launchpad that keeps your entire tax function proactive, not reactive.',
    middlewareNote: 'The Command Center is powered by the same data flowing through the middleware. Filing status, validation results, deadline proximity, and regulatory updates are all monitored and unified automatically.',
    apps: [
      {
        id: 'notifications-alerts',
        title: 'Notifications & Alerts',
        description: 'Two types of intelligent alerting: event-driven alerts that notify you when transactions are filed, validated, or fail — and deadline-driven reminders with configurable cascading warnings.',
        capabilities: [
          'Event alerts — instant notification when transactions are filed, validated, or fail',
          'Deadline reminders — configurable cascading warnings (e.g., 10 days, then 5 days, then overdue)',
          'Fully configurable per entity, jurisdiction, and obligation type',
          'Delivered in-platform, via email, or to your ERP inbox',
        ],
        middlewareNote: 'Alerts are driven by the same data flowing through the middleware. Filing status, validation results, and deadline proximity are all monitored automatically.',
        stat: { value: 'Configurable', label: 'cascade alerts' },
      },
      {
        id: 'global-tax-calendar',
        title: 'Global Tax Calendar',
        description: 'A pre-filled, configurable calendar of every filing deadline across multiple jurisdictions. Customize per entity, add or remove obligations, and let the calendar drive your notification engine.',
        capabilities: [
          'Pre-filled with filing deadlines across multiple jurisdictions',
          'Customize per entity — add, remove, or override obligations',
          'Visual timeline of your entire global tax function',
          'Drives the notification engine — the calendar is the source of truth for deadlines',
        ],
        middlewareNote: 'The calendar is pre-populated with country-specific deadlines and updated as regulations change. Your team customizes — the platform maintains.',
        stat: { value: 'Global', label: 'jurisdictions' },
      },
      {
        id: 'global-launchpad',
        title: 'Global Launchpad',
        description: 'A unified dashboard combining upcoming filings, open transactions, calendar deadlines, and a real-time regulatory news feed — built natively on SAP BTP for seamless ERP integration.',
        capabilities: [
          'Combines upcoming filings, open transactions, and calendar deadlines in one view',
          'Integrated news feed — regulatory changes and industry updates scraped in real-time',
          'Built natively on SAP BTP for seamless ERP integration',
          'Single entry point for your entire tax operations team',
        ],
        middlewareNote: 'The Launchpad unifies data from every hub — filings, alerts, calendar, news — into one operational view. Built on SAP BTP, it lives where your team already works.',
        stat: { value: 'SAP BTP', label: 'native' },
      },
    ],
  },
  {
    id: 'einvoicing-hub',
    name: 'e-Invoicing Hub',
    tagline: 'Create, validate, submit — every format, every country.',
    icon: FileText,
    color: '#06b6d4',
    description: 'End-to-end electronic invoicing from creation to authority acknowledgment. Country-specific modules built to every local standard, with real-time validation and direct portal submission.',
    middlewareNote: 'Invoice data is pulled directly from your ERP business processes — validated, formatted, and submitted without file exports. The middleware handles every country-specific format natively.',
    apps: [
      {
        id: 'einvoicing-cockpit',
        title: 'e-Invoicing Cockpit',
        description: 'Central dashboard for monitoring all e-Invoicing activity across entities and countries. Real-time status tracking, exception management, and compliance overview.',
        capabilities: [
          'Real-time invoice status across all entities',
          'Exception and rejection management',
          'Compliance monitoring per country',
          'Full audit trail from creation to acknowledgment',
        ],
        middlewareNote: 'The cockpit pulls data directly from your ERP business processes — giving you a single view of every invoice across every jurisdiction.',
        stat: { value: 'Real-time', label: 'monitoring' },
      },
      {
        id: 'country-einvoicing',
        title: 'Country-Specific e-Invoicing',
        description: 'Native support for every country-specific e-Invoicing mandate. From Greece myDATA to Italy SDI, Poland KSeF, and beyond — each module is built to the local standard.',
        capabilities: [
          'Greece myDATA: Sales, Purchase, Local Purchase invoices & C Postings',
          'Italy SDI, Poland KSeF, Romania RO e-Factura',
          'Every global format: UBL, CII, FatturaPA, CFDI and more',
          'Direct portal submission worldwide',
        ],
        middlewareNote: 'Invoice data is pulled directly from your ERP business process — validated, formatted, and submitted without file exports.',
        stat: { value: '<0.5%', label: 'rejection rate' },
      },
    ],
  },
  {
    id: 'ecompliance-hub',
    name: 'eCompliance Hub',
    tagline: 'Continuous monitoring. Full control. Audit-ready.',
    icon: ShieldCheck,
    color: '#10b981',
    description: 'Your compliance command center. Verify counterparties, track velocity metrics, monitor filing status, and maintain audit readiness across every jurisdiction from one place.',
    middlewareNote: 'Compliance checks are triggered automatically as part of your business processes. The middleware ensures continuous monitoring without manual lookups or gaps in verification.',
    apps: [
      {
        id: 'vies-verification',
        title: 'VIES Verification',
        description: 'Automated VAT Information Exchange System verification of trading partner VAT numbers across the EU, with batch processing and continuous monitoring.',
        capabilities: [
          'Automated batch VAT number verification',
          'Continuous monitoring of trading partner status',
          'Integration with invoice processing workflows',
          'Audit-ready verification logs',
        ],
        middlewareNote: 'VIES checks are triggered automatically as part of your invoice processing — no manual lookups, no gaps in verification.',
        stat: { value: 'Automated', label: 'batch verification' },
      },
      {
        id: 'velocity',
        title: 'Velocity',
        description: 'Real-time compliance velocity tracking and analytics. Monitor processing speed, identify bottlenecks, and ensure your compliance function meets every deadline.',
        capabilities: [
          'Real-time processing speed metrics',
          'Bottleneck identification and alerting',
          'Deadline tracking with automated notifications',
          'Compliance SLA monitoring per entity and country',
        ],
        middlewareNote: 'Because all compliance data flows through one platform, Velocity sees everything — processing times, exceptions, and trends across your entire compliance function.',
        stat: { value: 'taxera.app', label: 'live platform' },
        externalLink: 'https://www.taxera.app',
      },
    ],
  },
  {
    id: 'ereporting-hub',
    name: 'eReporting Hub',
    tagline: 'SAF-T, eTransport, and audit-file reporting — automated.',
    icon: Database,
    color: '#3b82f6',
    description: 'Automated generation and submission of structured audit files and transport documents. SAF-T, eTransport, and source data analytics — all from your live ERP data.',
    middlewareNote: 'The middleware extracts exactly the data fields needed for each reporting format — directly from your ERP. Schema changes and regulatory updates are absorbed automatically.',
    apps: [
      {
        id: 'saft',
        title: 'SAF-T',
        description: 'Automated SAF-T file generation from live ERP data with built-in schema validation, regulatory change tracking, and pre-submission quality checks.',
        capabilities: [
          'Automated SAF-T generation from live ERP data',
          'Pre-submission schema validation',
          'Regulatory changes tracked and applied automatically',
          'Multi-country SAF-T support (Portugal, Norway, Poland, Lithuania, etc.)',
        ],
        middlewareNote: 'Middleware extracts exactly the data fields needed for SAF-T — directly from your ERP. Schema changes are absorbed by us.',
        stat: { value: '95%', label: 'less prep time' },
      },
      {
        id: 'saft-reconciliation',
        title: 'SAF-T Reconciliation',
        description: 'Cross-validate your SAF-T data against source transactions, filed returns, and invoice records before submission.',
        capabilities: [
          'Three-way reconciliation: SAF-T vs. source data vs. filed returns',
          'Pre-submission discrepancy detection',
          'Automated variance analysis across periods',
          'Full audit trail for every reconciled item',
        ],
        middlewareNote: 'SAF-T reconciliation uses the same transaction data flowing through Taxera. No re-extraction, no manual consolidation.',
        stat: { value: 'Pre-submit', label: 'validation' },
      },
      {
        id: 'saft-source-data',
        title: 'SAF-T Source Data Analytics',
        description: 'Deep analytics on your SAF-T source data. Identify data quality issues, trends, and anomalies before they become compliance problems.',
        capabilities: [
          'Data quality scoring and trend analysis',
          'Anomaly detection across transaction data',
          'Source-to-SAF-T field mapping validation',
          'Historical comparison and drift detection',
        ],
        middlewareNote: 'Source data analytics operates on your live ERP data — catching issues at the source, not after the file is generated.',
        stat: { value: 'Proactive', label: 'issue detection' },
      },
      {
        id: 'etransport',
        title: 'eTransport',
        description: 'Electronic transport document management ensuring compliant goods movement across jurisdictions with real-time tracking and authority reporting.',
        capabilities: [
          'Electronic waybill generation and management',
          'Real-time goods movement tracking and compliance',
          'Integration with logistics and procurement processes',
          'Direct reporting to transport authorities',
        ],
        middlewareNote: 'Goods movement data pulled directly from your logistics processes. Transport compliance integrated with your invoicing and VAT data.',
        stat: { value: 'Live', label: 'tracking' },
      },
    ],
  },
  {
    id: 'tax-reporting-hub',
    name: 'Tax Reporting Hub',
    tagline: 'VAT returns, EC Sales, Intrastat — automated from source.',
    icon: BarChart3,
    color: '#8b5cf6',
    description: 'Automated tax reporting from transaction data. VAT returns, VAT groups, EC Sales lists, and Intrastat declarations — all generated from the same source, all consistent.',
    middlewareNote: 'Tax data is pulled from the same source as your e-Invoicing and SAF-T data. One integration ensures consistent numbers across all returns and declarations.',
    apps: [
      {
        id: 'vat-returns',
        title: 'VAT Returns',
        description: 'Automated multi-country VAT return preparation, consolidation, and filing with built-in deadline management.',
        capabilities: [
          'Automated return preparation from transaction data',
          'Multi-entity, multi-country consolidation',
          'Deadline management and automated submission',
          'Prefilled returns from reconciled transaction data',
        ],
        middlewareNote: 'VAT data is pulled from the same source as your e-Invoicing and SAF-T data. One integration, consistent numbers.',
        stat: { value: '100%', label: 'on-time filing' },
      },
      {
        id: 'vat-groups',
        title: 'VAT Groups',
        description: 'Native support for VAT group structures and inter-company flows, with automated consolidation across group entities.',
        capabilities: [
          'VAT group structure management',
          'Inter-company flow tracking and netting',
          'Consolidated group reporting',
          'Country-specific VAT group rule compliance',
        ],
        middlewareNote: 'VAT group calculations happen natively within the platform — no manual adjustments, no spreadsheet consolidation.',
        stat: { value: 'Native', label: 'group support' },
      },
      {
        id: 'ec-sales-list',
        title: 'EC Sales List',
        description: 'Automated EC Sales List reporting linked to your invoice and Intrastat data, with cross-validation against other filings.',
        capabilities: [
          'Automated ESL generation from invoice data',
          'Cross-validation against VAT returns and Intrastat',
          'Multi-entity consolidation',
          'Deadline tracking and submission management',
        ],
        middlewareNote: 'EC Sales data is cross-referenced against your e-Invoicing and Intrastat data. Discrepancies flagged before filing.',
        stat: { value: 'Cross-validated', label: 'accuracy' },
      },
      {
        id: 'intrastat',
        title: 'Intrastat',
        description: 'Automated Intrastat declaration preparation with goods movement data integration and commodity code management.',
        capabilities: [
          'Automated Intrastat from goods movement data',
          'Commodity code management and validation',
          'Cross-border flow tracking per entity',
          'Integration with logistics and procurement data',
        ],
        middlewareNote: 'Intrastat data flows from your ERP goods movements and invoices — matched and validated before declaration.',
        stat: { value: 'Automated', label: 'declarations' },
      },
    ],
  },
  {
    id: 'tax-operations-hub',
    name: 'Tax Operations Hub',
    tagline: 'Reconciliation. Cross-checking. Operational control.',
    icon: RefreshCw,
    color: '#f59e0b',
    description: 'The operational backbone of your tax function. Monthly reconciliation, e-Invoicing reconciliation, and cross-checking between VAT and EC Sales — all automated.',
    middlewareNote: 'All compliance data flows through one platform, so reconciliation happens automatically using the same transaction data — no re-extraction, no manual consolidation.',
    apps: [
      {
        id: 'monthly-reconciliation',
        title: 'Monthly Reconciliation',
        description: 'Accelerate your month-end close with automated reconciliation of tax-relevant transactions, reducing manual effort and eliminating errors.',
        capabilities: [
          'Automated matching of tax-relevant transactions',
          'Exception management with configurable workflows',
          'Variance analysis across entities and periods',
          'Full audit trail for every reconciled item',
        ],
        middlewareNote: 'Month-end reconciliation uses the same transaction data flowing through Taxera. No re-extraction, no manual consolidation.',
        stat: { value: '80%', label: 'faster close' },
      },
      {
        id: 'einvoicing-reconciliation',
        title: 'e-Invoicing Reconciliation',
        description: 'Automated three-way matching of invoices, purchase orders, and delivery notes with real-time anomaly detection.',
        capabilities: [
          'AI-powered matching across invoices, POs, and goods receipts',
          'Anomaly detection in real-time — not months later in an audit',
          'Cross-checking between e-Invoicing, VAT, and SAF-T data',
          'Configurable tolerance rules per entity, per country',
        ],
        middlewareNote: 'Middleware sees your invoice data, PO data, and delivery data — all from your ERP. Reconciliation happens automatically.',
        stat: { value: 'Real-time', label: 'anomaly detection' },
      },
      {
        id: 'recon-vat-ecsl',
        title: 'Recon: VAT to ECSL',
        description: 'Cross-validate your VAT return data against EC Sales List declarations to ensure consistency before filing both reports.',
        capabilities: [
          'Automated VAT-to-ECSL cross-checking',
          'Discrepancy identification before filing',
          'Entity-level and country-level reconciliation',
          'Historical trend analysis for recurring mismatches',
        ],
        middlewareNote: 'Because VAT returns and EC Sales Lists are generated from the same data layer, reconciliation is automatic — not an afterthought.',
        stat: { value: 'Pre-filing', label: 'cross-check' },
      },
    ],
  },
];

/* ═══════════════════════════════════════════ */

const ProductsPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const hubParam = searchParams.get('hub');
  const initialHub = PLATFORM_HUBS.find(h => h.id === hubParam)?.id || PLATFORM_HUBS[0].id;
  const [activeHub, setActiveHub] = useState(initialHub);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyName, setNotifyName] = useState('');
  const [notifyStatus, setNotifyStatus] = useState(null);
  const selectedHub = PLATFORM_HUBS.find(h => h.id === activeHub);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (hubParam && PLATFORM_HUBS.find(h => h.id === hubParam)) {
      setActiveHub(hubParam);
      setTimeout(() => {
        document.getElementById('hub-explorer')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [hubParam]);

  return (
    <PageLayout>
      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden pt-8 pb-16" data-testid="products-hero">
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-taxera-teal/10 border border-taxera-teal/20 rounded-full mb-8">
              <div className="w-2 h-2 rounded-full bg-taxera-teal animate-pulse" />
              <span className="text-xs font-semibold tracking-[0.15em] uppercase text-taxera-teal">{t('products.badge')}</span>
            </div>
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-[1.08] mb-6" data-testid="products-h1">
            Taxera Compliance Platform —<br />
            <span className="taxera-gradient-text">Product Hubs & Apps</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-12">
            Every compliance need — e-Invoicing, reporting, returns, reconciliation — handled by one connected platform. One ERP integration. Full visibility. No gaps.
          </motion.p>

          {/* Hub pills */}
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="flex flex-wrap justify-center gap-3">
            {PLATFORM_HUBS.map((hub, i) => {
              const Icon = hub.icon;
              return (
                <motion.button
                  key={hub.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.06 }}
                  onClick={() => {
                    setActiveHub(hub.id);
                    document.getElementById('hub-explorer')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full border bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                  style={{ borderColor: `${hub.color}35` }}
                  data-testid={`hub-pill-${hub.id}`}
                >
                  <Icon className="w-3.5 h-3.5" style={{ color: hub.color }} strokeWidth={1.5} />
                  <span className="text-sm font-semibold text-taxera-charcoal">{hub.name.replace(' Hub', '')}</span>
                </motion.button>
              );
            })}
          </motion.div>
        </div>
        <ScrollHint targetId="hub-explorer" label="Explore the platform" />
      </section>

      <section id="hub-explorer" className="bg-slate-50 relative" data-testid="hub-explorer">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-24">
          {/* Section header */}
          <div className="mb-16 max-w-2xl">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-3">Platform Architecture</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-taxera-charcoal leading-tight mb-4">
              Explore the platform
            </h2>
            <p className="text-base text-slate-500 leading-relaxed">
              Each hub solves a distinct compliance challenge. Together, they create a seamless, cross-validated compliance function powered by one shared data layer.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left: Hub selector tabs (sticky on desktop) */}
            <div className="lg:w-72 shrink-0">
              <div className="lg:sticky lg:top-24 space-y-2">
                {PLATFORM_HUBS.map((hub, i) => {
                  const Icon = hub.icon;
                  const isActive = activeHub === hub.id;
                  return (
                    <motion.button
                      key={hub.id}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      onClick={() => setActiveHub(hub.id)}
                      className={`group w-full text-left px-5 py-4 rounded-xl border transition-all duration-300 ${
                        isActive
                          ? 'border-slate-200 bg-white shadow-sm'
                          : 'border-transparent hover:border-slate-200 hover:bg-white/60'
                      }`}
                      style={isActive ? { boxShadow: `0 2px 12px ${hub.color}12` } : {}}
                      data-testid={`hub-selector-${hub.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center transition-all" style={{ backgroundColor: isActive ? `${hub.color}15` : '#f1f5f9' }}>
                          <Icon className="w-4.5 h-4.5" strokeWidth={1.5} style={{ color: isActive ? hub.color : '#94a3b8' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-bold transition-colors ${isActive ? 'text-taxera-charcoal' : 'text-slate-400 group-hover:text-slate-600'}`}>
                            {hub.name}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5 line-clamp-1">{hub.tagline}</div>
                        </div>
                        {isActive && <div className="w-1.5 h-8 rounded-full" style={{ backgroundColor: hub.color }} />}
                      </div>
                    </motion.button>
                  );
                })}

                {/* Integration badge */}
                <div className="mt-6 px-5 py-4 bg-white rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2.5 mb-2">
                    <Link2 className="w-4 h-4 text-taxera-teal" strokeWidth={1.5} />
                    <span className="text-xs font-bold text-taxera-teal">One Integration</span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">All five hubs share one ERP connection and one data layer.</p>
                </div>
              </div>
            </div>

            {/* Right: Selected hub content */}
            <div className="flex-1 min-w-0">
              <AnimatePresence mode="wait">
                {selectedHub && (
                  <motion.div
                    key={selectedHub.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35 }}
                  >
                    {/* Hub header card */}
                    <div
                      className="rounded-2xl border border-slate-200 bg-white p-8 lg:p-10 mb-8 relative overflow-hidden"
                      data-testid={`hub-detail-${selectedHub.id}`}
                    >
                      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${selectedHub.color}, ${selectedHub.color}40, transparent)` }} />
                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${selectedHub.color}12` }}>
                          {(() => { const Icon = selectedHub.icon; return <Icon className="w-7 h-7" strokeWidth={1.5} style={{ color: selectedHub.color }} />; })()}
                        </div>
                        <div>
                          <h3 className="font-heading text-2xl lg:text-3xl font-bold text-taxera-charcoal mb-2">{selectedHub.name}</h3>
                          <p className="text-base text-slate-500 leading-relaxed max-w-xl">{selectedHub.description}</p>
                          {selectedHub.id === 'einvoicing-hub' && (
                            <Link to="/solutions/e-invoicing" className="group mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0066CC] hover:underline" data-testid="einvoicing-hub-country-link">
                              See e-Invoicing rules by country <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Coming Soon — Tech Zero Notify */}
                      <div className="mt-6 p-5 rounded-xl bg-gradient-to-r from-slate-50 to-taxera-teal/[0.04] border border-slate-200" data-testid="hub-video-coming-soon">
                        <div className="flex items-center gap-2 mb-2">
                          <Play className="w-4 h-4 text-slate-400" />
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Product Video — Coming Soon</span>
                        </div>
                        <p className="text-sm text-slate-500 mb-4">
                          Join the <span className="font-semibold text-taxera-charcoal">Taxera</span> community to be the first to see it.
                        </p>
                        {notifyStatus === 'success' ? (
                          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600" data-testid="notify-success">
                            <CheckCircle2 className="w-4 h-4" /> You're in! We'll notify you when it's ready.
                          </div>
                        ) : (
                          <form onSubmit={async (e) => {
                            e.preventDefault();
                            if (!notifyEmail) return;
                            setNotifyStatus('loading');
                            try {
                              const res = await fetch(`${API}/api/notify/product-videos`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ name: notifyName, email: notifyEmail, hub: selectedHub.id }),
                              });
                              if (res.ok) { setNotifyStatus('success'); }
                              else { setNotifyStatus('error'); }
                            } catch { setNotifyStatus('error'); }
                          }} className="flex flex-col sm:flex-row gap-2">
                            <input
                              type="text"
                              value={notifyName}
                              onChange={e => setNotifyName(e.target.value)}
                              placeholder="First name"
                              className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal w-full sm:w-32"
                              data-testid="notify-name-input"
                            />
                            <input
                              type="email"
                              value={notifyEmail}
                              onChange={e => setNotifyEmail(e.target.value)}
                              placeholder="Email"
                              required
                              className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal flex-1"
                              data-testid="notify-email-input"
                            />
                            <button
                              type="submit"
                              disabled={notifyStatus === 'loading'}
                              className="px-5 py-2 rounded-lg taxera-gradient text-white text-sm font-semibold hover:shadow-md transition-all disabled:opacity-50 whitespace-nowrap"
                              data-testid="notify-submit-btn"
                            >
                              {notifyStatus === 'loading' ? 'Joining...' : 'Notify me'}
                            </button>
                          </form>
                        )}
                        {notifyStatus === 'error' && (
                          <p className="text-xs text-red-500 mt-2">Something went wrong. Please try again.</p>
                        )}
                      </div>
                    </div>

                    {/* App cards — Bento grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid={`hub-apps-${selectedHub.id}`}>
                      {selectedHub.apps.map((app, appIdx) => (
                        <motion.div
                          key={app.id}
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 + appIdx * 0.08 }}
                          className={`group rounded-2xl border border-slate-200 bg-white p-6 lg:p-7 hover:border-slate-300 hover:shadow-md transition-all duration-300 ${
                            selectedHub.apps.length === 1 || (selectedHub.apps.length === 3 && appIdx === 0) ? 'md:col-span-2' : ''
                          }`}
                          data-testid={`app-card-${app.id}`}
                        >
                          {/* Top accent line */}
                          <div className="h-[2px] w-12 rounded-full mb-5" style={{ backgroundColor: selectedHub.color }} />

                          {/* Title + stat */}
                          <div className="flex items-start justify-between gap-4 mb-4">
                            <div>
                              <h4 className="font-heading text-lg font-bold text-taxera-charcoal mb-1.5">{app.title}</h4>
                              <p className="text-sm text-slate-500 leading-relaxed">{app.description}</p>
                            </div>
                            <div className="shrink-0 text-right hidden sm:block">
                              <div className="font-heading text-xl font-bold" style={{ color: selectedHub.color }}>{app.stat.value}</div>
                              <div className="text-2xs text-slate-400 font-medium mt-0.5">{app.stat.label}</div>
                            </div>
                          </div>

                          {/* Capabilities */}
                          <div className="mb-5">
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                              {app.capabilities.map((cap, i) => (
                                <li key={`cap-${cap.slice(0, 25)}`} className="flex items-start gap-2">
                                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: selectedHub.color }} strokeWidth={1.5} />
                                  <span className="text-xs text-slate-600 leading-relaxed">{cap}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* External link */}
                          {app.externalLink && (
                            <a href={app.externalLink} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg text-xs font-semibold transition-colors"
                              style={{ backgroundColor: `${selectedHub.color}12`, color: selectedHub.color }}
                              data-testid={`external-link-${app.id}`}
                            >
                              <ExternalLink className="w-3 h-3" /> Visit taxera.app
                            </a>
                          )}
                        </motion.div>
                      ))}
                    </div>

                    {/* Consolidated middleware note — one per hub */}
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="mt-6 rounded-2xl border border-taxera-teal/20 bg-gradient-to-r from-taxera-teal/[0.04] to-transparent p-6 lg:p-7 flex items-start gap-4"
                      data-testid={`hub-middleware-note-${selectedHub.id}`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-taxera-teal/10 flex items-center justify-center shrink-0">
                        <Layers className="w-5 h-5 text-taxera-teal" strokeWidth={1.5} />
                      </div>
                      <div>
                        <div className="text-xs font-bold tracking-[0.12em] text-taxera-teal uppercase mb-1.5">Native ERP Integration</div>
                        <p className="text-sm text-slate-600 leading-relaxed">{selectedHub.middlewareNote}</p>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CROSS-CHECKING STORY ═══ */}
      <section className="py-24 bg-slate-900" data-testid="cross-checking">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <RefreshCw className="w-8 h-8 text-taxera-teal mx-auto mb-5" strokeWidth={1.5} />
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
              The platform advantage: cross-checking
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Tax authorities compare your e-Invoicing data against your VAT returns, your SAF-T files against your EC Sales lists. Because every hub shares one data layer, inconsistencies are caught before they leave your system.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { id: 'inv-vs-ret', title: 'Invoice vs. Return', desc: 'e-Invoicing Hub cross-references against Tax Reporting Hub data. Discrepancies flagged in real-time.', icon: FileText },
              { id: 'saft-vs-fil', title: 'SAF-T vs. Filings', desc: 'eReporting audit file data validated against filed returns and invoice records. Consistent across every submission.', icon: Database },
              { id: 'ops-vs-all', title: 'Operations vs. Filings', desc: 'Tax Operations Hub reconciles your VAT returns against EC Sales data, and invoices against source documents. No conflicting numbers.', icon: Globe },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={item.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:border-white/20 transition-all"
                >
                  <Icon className="w-5 h-5 text-taxera-teal mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-base font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="py-20" data-testid="products-faq">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-taxera-teal mb-3">Frequently Asked Questions</p>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal">Common questions about Taxera</h2>
          </div>
          <div className="space-y-3" data-testid="products-faq-list">
            {PRODUCTS_FAQ.map((faq, i) => (
              <ProductsFAQItem key={`pfaq-${faq.q.slice(0, 25)}`} question={faq.q} answer={faq.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-24 bg-slate-50" data-testid="products-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-4">
            See it for your compliance landscape
          </h2>
          <p className="text-base text-slate-600 mb-10">
            Every organization's compliance needs are different. Let's map Taxera to yours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/evaluate" className="group inline-flex items-center gap-2 px-8 py-4 taxera-gradient text-white rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105" data-testid="products-evaluate-btn">
              Take the Diagnostic <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/platform" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-taxera-charcoal border border-slate-200 rounded-full font-semibold hover:bg-slate-100 transition-all" data-testid="products-platform-btn">
              See How It Works
            </Link>
          </div>
        </div>
      </section>

    </PageLayout>
  );
};

export default ProductsPage;
